import { useState } from 'react';

import { cn } from '../cn';
import { Button } from '../Input2';
import { Arrows, General } from '../StrokeIcons';
import { WaveSpinner } from '../WaveSpinner';

/** The raw image-cache blob variants for a contact photo (see `@poseidon/image-cache`). */
export type ContactImageCacheVariants = {
  sm: string;
  md: string;
  lg: string;
  /** Background-removed cutout — CONTACT photos only; most blobs never have it. */
  nobg?: string;
  /** The `nobg` cutout cropped to its content (fully-transparent edges removed). */
  trim?: string;
};

/** All image URLs known for a contact — the shape returned by `contact.imageVariants`. */
export type ContactImageVariants = {
  /** The app-wide avatar source (durable GCS `md` copy). */
  primaryPictureUrl: string | null;
  /** Denormalized background-removed cutout on the contact row. */
  noBgPictureUrl: string | null;
  /** Denormalized trimmed cutout on the contact row. */
  trimmedPictureUrl: string | null;
  /** Raw image-cache blob variants, or null when the cache is unconfigured/absent. */
  cacheVariants: ContactImageCacheVariants | null;
};

export type ContactImagesSectionPureProps = {
  images: ContactImageVariants | null | undefined;
  isLoading?: boolean;
  /**
   * Optional enhance/generate action. Only rendered when provided — never a no-op button. Wire it to
   * the mutation that regenerates the contact's photo + cutout variants.
   */
  onEnhance?: () => void;
  isEnhancing?: boolean;
};

type ImageTile = {
  key: string;
  label: string;
  url: string | null;
  /** Transparent variants render on a checkerboard so the see-through areas read as transparent. */
  transparent: boolean;
};

/**
 * A single image variant: a labeled preview that opens the full-resolution image in a new tab, or a
 * dashed "Not generated" placeholder when the variant doesn't exist yet.
 */
function ImageTilePure({ tile }: { tile: ImageTile }) {
  return (
    <div className="flex flex-col gap-1">
      {tile.url != null && (
        <a
          href={tile.url}
          target="_blank"
          rel="noopener noreferrer"
          title="Open full image in a new tab"
          className={cn(
            'group relative flex aspect-square items-center justify-center overflow-hidden rounded-md border border-gray-200 transition-shadow hover:shadow-md dark:border-gray-700',
            tile.transparent ? 'bg-checkerboard-light dark:bg-checkerboard-dark' : 'bg-gray-50 dark:bg-gray-800',
          )}>
          {/* Raw variant inspector — a plain <img> (not Avatar) so we see the exact bytes, transparency
              and all. eslint-disable is unnecessary: packages/components already ships <img> elsewhere. */}
          <img src={tile.url} alt={tile.label} loading="lazy" className="size-full object-contain" />
          <span className="absolute right-1 top-1 rounded-sm bg-black/50 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100">
            <General.LinkExternal01 className="size-3" />
          </span>
        </a>
      )}
      {tile.url == null && (
        <div className="flex aspect-square items-center justify-center rounded-md border border-dashed border-gray-200 bg-gray-50/50 px-1 text-center text-2xs text-gray-400 dark:border-gray-700 dark:bg-gray-800/40 dark:text-gray-500">
          Not generated
        </div>
      )}
      <span className="truncate text-center text-2xs text-gray-500 dark:text-gray-400" title={tile.label}>
        {tile.label}
      </span>
    </div>
  );
}

/**
 * Collapsible inspector of every image variant a contact has — the denormalized columns that drive
 * the app (primary photo, background-removed cutout, trimmed cutout) plus the raw image-cache blob
 * variants (sm/md/lg). Mirrors the collapsed-by-default LinkedIn Identifiers section; most users never
 * open it, but it makes it obvious which cutouts exist and which still need generating. When `onEnhance`
 * is provided, an Enhance button in the header regenerates the photo + cutouts.
 */
export function ContactImagesSectionPure({ images, isLoading, onEnhance, isEnhancing }: ContactImagesSectionPureProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const cache = images?.cacheVariants ?? null;
  const tiles: ImageTile[] = [
    { key: 'primary', label: 'Primary (source)', url: images?.primaryPictureUrl ?? null, transparent: false },
    { key: 'nobg', label: 'Background removed', url: images?.noBgPictureUrl ?? cache?.nobg ?? null, transparent: true },
    { key: 'trim', label: 'Trimmed cutout', url: images?.trimmedPictureUrl ?? cache?.trim ?? null, transparent: true },
    { key: 'sm', label: 'Cache · sm', url: cache?.sm ?? null, transparent: false },
    { key: 'md', label: 'Cache · md', url: cache?.md ?? null, transparent: false },
    { key: 'lg', label: 'Cache · lg', url: cache?.lg ?? null, transparent: false },
  ];
  const availableCount = tiles.filter((t) => t.url != null).length;

  return (
    <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
      {/* Header — clickable to expand/collapse */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex flex-1 items-center gap-1.5 text-left"
          onClick={() => setIsExpanded((v) => !v)}
          aria-expanded={isExpanded}>
          {isExpanded && <Arrows.ChevronDown className="size-3.5 text-gray-400" />}
          {!isExpanded && <Arrows.ChevronRight className="size-3.5 text-gray-400" />}
          <span className="text-sm font-semibold text-gray-900 dark:text-white">Images</span>
          {!isExpanded && availableCount > 0 && (
            <span className="text-xs text-gray-400 dark:text-gray-500">({availableCount})</span>
          )}
        </button>
        {onEnhance != null && (
          <Button
            size="xs"
            text
            icon={isEnhancing ? <WaveSpinner className="size-3.5" /> : <General.Zap className="size-3.5" />}
            onClick={onEnhance}
            disabled={isEnhancing}
            tooltip="Regenerate the photo and cutout variants">
            Enhance
          </Button>
        )}
      </div>

      {isExpanded && (
        <div className="mt-2">
          {isLoading && (
            <div className="flex items-center justify-center py-4">
              <WaveSpinner className="size-4" />
            </div>
          )}

          {!isLoading && images == null && (
            <p className="py-2 text-xs text-gray-400 dark:text-gray-500">No image data</p>
          )}

          {!isLoading && images != null && (
            <div className="grid grid-cols-3 gap-2">
              {tiles.map((tile) => (
                <ImageTilePure key={tile.key} tile={tile} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ContactImagesSectionPure;
